class SwiperLoader {
  constructor() {
    this.loaded = false;
    this.loading = false;
    this.pendingInits = [];
    this.observers = new Map();
  }

  async loadSwiper() {
    if (this.loaded) {
      return Promise.resolve(window.Swiper);
    }

    if (this.loading) {
      return new Promise((resolve) => {
        this.pendingInits.push(resolve);
      });
    }

    this.loading = true;

    return new Promise((resolve, reject) => {
      const cssHref =
        document.querySelector('link[href*="swiper-bundle.min.css"]')?.href ||
        document
          .querySelector('script[src*="swiper-loader.js"]')
          ?.src.replace("swiper-loader.js", "swiper-bundle.min.css") ||
        "/assets/swiper-bundle.min.css";

      const scriptSrc =
        document.querySelector('script[src*="swiper-bundle.min.js"]')?.src ||
        document
          .querySelector('script[src*="swiper-loader.js"]')
          ?.src.replace("swiper-loader.js", "swiper-bundle.min.js") ||
        "/assets/swiper-bundle.min.js";

      const cssLink = document.createElement("link");
      cssLink.rel = "stylesheet";
      cssLink.href = cssHref;
      cssLink.media = "print";
      cssLink.onload = function () {
        this.media = "all";
      };

      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;

      script.onload = () => {
        this.loaded = true;
        this.loading = false;
        window.SWIPER_LOADED = true;
        window.dispatchEvent(new CustomEvent("swiper:loaded"));

        resolve(window.Swiper);

        this.pendingInits.forEach((callback) => callback(window.Swiper));
        this.pendingInits = [];
      };

      script.onerror = () => {
        this.loading = false;
        reject(new Error("Failed to load Swiper"));
      };

      document.head.appendChild(cssLink);
      document.head.appendChild(script);
    });
  }

  async initWhenVisible(element, initCallback, options = {}) {
    const { rootMargin = "50px", threshold = 0, once = true } = options;

    if (!element) {
      console.warn(
        "SwiperLoader: No element provided for intersection observer",
      );
      return;
    }

    const observer = new IntersectionObserver(
      async (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting) {
            try {
              await this.loadSwiper();
              if (typeof initCallback === "function") {
                initCallback(window.Swiper);
              }

              if (once) {
                observer.unobserve(element);
                this.observers.delete(element);
              }
            } catch (error) {
              console.error("SwiperLoader: Error initializing Swiper", error);
            }
          }
        });
      },
      { rootMargin, threshold },
    );

    observer.observe(element);
    this.observers.set(element, observer);

    return observer;
  }

  async initImmediately(initCallback) {
    if (!initCallback || typeof initCallback !== "function") {
      console.warn("SwiperLoader: No callback provided");
      return;
    }

    try {
      await this.loadSwiper();
      initCallback(window.Swiper);
    } catch (error) {
      console.error("SwiperLoader: Error loading Swiper", error);
    }
  }

  cleanup(element) {
    if (this.observers.has(element)) {
      const observer = this.observers.get(element);
      observer.disconnect();
      this.observers.delete(element);
    }
  }

  cleanupAll() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
  }
}

if (typeof window.swiperLoader === "undefined") {
  window.swiperLoader = new SwiperLoader();
}

if (typeof window.SWIPER_LOADED === "undefined") {
  window.SWIPER_LOADED = false;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.dispatchEvent(new CustomEvent("swiperloader:ready"));
  });
} else {
  window.dispatchEvent(new CustomEvent("swiperloader:ready"));
}
